
import React, { useState, useEffect, useRef } from 'react';
import {
    Target, Zap, Settings, CheckCircle,
    Trash2, AlertTriangle, Pencil,
    ArrowRight, Calendar, Plus, Clock, DollarSign, Car, MinusCircle, Pin
} from 'lucide-react';
import storage from '../../services/storageService';
import { useModal } from '../../contexts/ModalContext';

// --- Sub-Components (UI) ---

const ToolHeader = ({ isDark, title, platform }) => {
    let bgGradient = 'from-blue-500 to-blue-700';
    if (platform === '99') bgGradient = 'from-yellow-400 to-orange-500';

    return (
        <div className="flex items-center gap-3">
            <div className={`relative w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-lg overflow-hidden`}>
                <img src="icon-192.png" className="w-full h-full object-cover" alt="Logo" />
            </div>
            <div>
                <h2 className={`text-xl font-bold leading-none ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {title}
                </h2>
                <span className={`text-[10px] uppercase font-bold tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    Gestão de Missões
                </span>
            </div>
        </div>
    );
};

const Card = ({ children, className = "", isDark, borderColor, onClick }) => (
    <div onClick={onClick} className={`rounded-2xl p-5 border shadow-sm transition-all duration-300 ${isDark ? 'bg-slate-800/50 backdrop-blur-sm border-slate-700' : 'bg-white border-slate-100'} ${className} ${borderColor ? borderColor : ''}`}>
        {children}
    </div>
);

const ProgressBar = ({ current, total, color = "bg-green-500", isDark }) => {
    const safeTotal = total > 0 ? total : 1;
    const percentage = Math.min(100, Math.max(0, (current / safeTotal) * 100));
    return (
        <div className={`w-full rounded-full h-3 overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
            <div
                className={`h-full transition-all duration-700 cubic-bezier(0.4, 0, 0.2, 1) ${color} shadow-[0_0_10px_rgba(0,0,0,0.2)]`}
                style={{ width: `${percentage}%` }}
            />
        </div>
    );
};

const AddTripsModal = ({ isOpen, onClose, onConfirm, isDark, theme }) => {
    const [val, setVal] = useState('');
    const inputRef = React.useRef(null);
    
    React.useEffect(() => {
        if (isOpen) {
            setVal('');
            // iOS PWA Fix: setTimeout garante que o modal está renderizado antes do foco
            const t = setTimeout(() => inputRef.current?.focus(), 150);
            return () => clearTimeout(t);
        }
    }, [isOpen]);
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-end sm:items-center justify-center backdrop-blur-sm animate-fade-in"
             onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className={`rounded-t-3xl sm:rounded-3xl p-6 w-full sm:max-w-xs shadow-2xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-100'} animate-fade-in-up`}
                 style={{paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))'}}>
                <h3 className={`text-lg font-bold mb-4 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>Adicionar Viagens</h3>
                <input
                    ref={inputRef}
                    type="number"
                    inputMode="numeric"
                    enterKeyHint="done"
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    placeholder="Quantidade"
                    className={`w-full rounded-xl p-4 text-2xl font-bold text-center mb-6 focus:outline-none focus:ring-2 ${theme.borderFocus} border ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-slate-200 text-slate-900'}`}
                />
                <div className="flex gap-3">
                    <button onClick={onClose} className={`flex-1 py-3 rounded-xl font-bold ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-slate-600'}`}>Cancelar</button>
                    <button
                        onClick={() => {
                            const num = parseInt(val);
                            if (!isNaN(num) && num > 0) onConfirm(num);
                            onClose();
                            setVal('');
                        }}
                        className={`flex-1 py-3 rounded-xl font-bold text-white shadow-lg ${theme.primary}`}
                    >
                        Confirmar
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Main Component ---

export default function MissionTracker({ isDark, isExpired, onOpenPremium, isInitialLoadDone }) {
    const { setConfirmModal } = useModal();
    // --- State ---
    // State
    const [missions, setMissions] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'list', 'details', 'create', 'edit'
    const [activeMissionId, setActiveMissionId] = useState(null);
    const [isQuickEditingTrips, setIsQuickEditingTrips] = useState(false);
    const [editingMission, setEditingMission] = useState({ title: '', platform: 'uber', currentTrips: 0, startDate: '', deadline: '', tiers: [] });
    const [now, setNow] = useState(new Date());
    const [showAddTripsModal, setShowAddTripsModal] = useState(false);
    const quickEditInputRef = useRef(null);

    // --- Helpers ---
    const emptyMissionTemplate = {
        title: "",
        platform: "uber",
        currentTrips: 0,
        startDate: "",
        deadline: "",
        tiers: []
    };

    const getSafeDate = (dateVal) => {
        if (!dateVal) return new Date();
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? new Date() : d;
    };

    const formatDateForInput = (isoString) => {
        if (!isoString) return "";
        const d = new Date(isoString);
        if (isNaN(d.getTime())) return "";
        const pad = (n) => n < 10 ? '0' + n : n;
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const formatShortDate = (dateObj) => {
        const d = getSafeDate(dateObj);
        const pad = (n) => n < 10 ? '0' + n : n;
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}h`;
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    };    // Load Data
    useEffect(() => {
        const missions = storage.getItemSync('mission_tracker_storage_v2');
        if (missions) {
            setMissions(missions);
        } else {
            // Migrar dados antigos se existirem
            const oldUber = storage.getItemSync('mission_tracker_uber');
            const old99 = storage.getItemSync('mission_tracker_99');
            const migrated = [];

            if (oldUber) {
                if (oldUber.title) migrated.push({ ...oldUber, id: crypto.randomUUID(), platform: 'uber' });
            }
            if (old99) {
                if (old99.title) migrated.push({ ...old99, id: crypto.randomUUID(), platform: '99' });
            }

            if (migrated.length > 0) {
                setMissions(migrated);
                storage.setItem('mission_tracker_storage_v2', migrated);
            }
        }
        setIsLoaded(true);
    }, [isInitialLoadDone]);

    // Save Data
    useEffect(() => {
        if (!isLoaded) return; // Prevent saving empty state during initial load

        storage.setItem('mission_tracker_storage_v2', missions);
    }, [missions, isLoaded]);

    // Focus Quick Edit
    useEffect(() => {
        if (isQuickEditingTrips && quickEditInputRef.current) quickEditInputRef.current.focus();
    }, [isQuickEditingTrips]);

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    // --- Styles ---
    const styles = {
        bg: 'bg-transparent',
        text: isDark ? 'text-white' : 'text-slate-900',
        subtext: isDark ? 'text-slate-400' : 'text-slate-500',
        inputBg: isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-white border-slate-200 text-slate-900',
        iconBtn: isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-400 border-slate-700' : 'bg-white hover:bg-slate-50 text-slate-600 border-slate-200',
        buttonDanger: isDark ? 'bg-slate-900/50 hover:bg-red-900/20 active:bg-red-900/30 border-slate-800 text-slate-400 hover:text-red-400 hover:border-red-900/30' : 'bg-white hover:bg-red-50 border-slate-200 text-slate-400 hover:text-red-500',
        borderSeparator: isDark ? 'border-slate-800' : 'border-slate-100',
    };

    // --- Logic for Active Mission ---

    // Se estamos vendo detalhes ou editando, precisamos saber qual missão é
    const activeMission = viewMode === 'create'
        ? editingMission
        : (activeMissionId ? missions.find(m => m.id === activeMissionId) : null);

    // Se estiver editando uma missão existente, usamos o state temporário `editingMission`
    // Se estiver visualizando detalhes, usamos o `activeMission` direto do array `missions`
    // Mas para facilitar, vamos usar `editingMission` como nosso rascunho sempre que entrarmos em modo 'edit' ou 'create'
    // E quando estamos em 'details', usamos o `activeMission` para leitura.

    // Cálculos (apenas se tiver activeMission)
    const mission = viewMode === 'create' || viewMode === 'edit' ? editingMission : activeMission;

    // Previne crash se mission for null
    if (!mission && viewMode !== 'list') {
        setViewMode('list');
        return null;
    }

    const hasMission = mission && mission.tiers && mission.tiers.length > 0;
    const getCumulativeMeta = (index) => {
        if (!hasMission) return 0;
        let total = 0;
        for (let i = 0; i <= index; i++) {
            if (mission.tiers[i]) total += (parseInt(mission.tiers[i].targetTrips) || 0);
        }
        return total;
    };

    const getCumulativeReward = (tierIndex) => {
        if (!hasMission) return 0;
        let total = 0;
        for (let i = 0; i <= tierIndex; i++) {
            if (mission.tiers[i]) total += (parseFloat(mission.tiers[i].reward) || 0);
        }
        return total;
    };

    const totalMeta = hasMission ? getCumulativeMeta(mission.tiers.length - 1) : 0;
    const totalPotentialBonus = hasMission ? getCumulativeReward(mission.tiers.length - 1) : 0;

    const activeTierIndex = hasMission ? mission.tiers.findIndex((t, idx) => mission.currentTrips < getCumulativeMeta(idx)) : -1;
    const isAllCompleted = hasMission && activeTierIndex === -1;
    const activeTier = hasMission ? (isAllCompleted ? mission.tiers[mission.tiers.length - 1] : mission.tiers[activeTierIndex]) : null;
    const activeCumulativeMeta = hasMission ? (isAllCompleted ? totalMeta : getCumulativeMeta(activeTierIndex)) : 0;

    const startDate = mission ? getSafeDate(mission.startDate) : new Date();
    const deadlineDate = mission ? getSafeDate(mission.deadline) : new Date();

    const msRemaining = deadlineDate.getTime() - now.getTime();
    const hoursRemaining = msRemaining / (1000 * 60 * 60);
    const usePaceByDay = hoursRemaining > 24;
    const daysRemaining = Math.max(1, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));

    const tripsRemainingForTier = (hasMission && activeCumulativeMeta) ? Math.max(0, activeCumulativeMeta - mission.currentTrips) : 0;
    const tripsRemainingForAll = hasMission ? Math.max(0, totalMeta - mission.currentTrips) : 0;

    const paceNeeded = activeCumulativeMeta
        ? (usePaceByDay ? (tripsRemainingForTier / daysRemaining) : (tripsRemainingForTier / Math.max(1, hoursRemaining)))
        : 0;

    const generalPaceNeeded = totalMeta
        ? (usePaceByDay ? (tripsRemainingForAll / daysRemaining) : (tripsRemainingForAll / Math.max(1, hoursRemaining)))
        : 0;

    const isPaceDanger = usePaceByDay ? paceNeeded > 25 : paceNeeded > 2.5;
    const isPaceWarning = usePaceByDay ? paceNeeded > 15 : paceNeeded > 1.5;

    // O valor por viagem agora é calculado sobre o valor "adicional" da fase mesmo
    const tripsInTierTotal = activeTier ? (parseInt(activeTier.targetTrips) || 1) : 1;
    const valuePerTripInTier = (activeTier && tripsInTierTotal > 0) ? ((parseFloat(activeTier.reward) || 0) / tripsInTierTotal) : 0;

    // Platform Themes
    const getPlatformColors = (plat) => {
        if (plat === '99') {
            return {
                primary: 'bg-yellow-400',
                primaryHover: 'hover:bg-yellow-500',
                primaryText: 'text-yellow-600',
                bgGradient: 'from-yellow-400 to-orange-500',
                borderFocus: 'focus:border-yellow-500',
                progressBar: 'bg-yellow-500',
                lightBg: 'bg-yellow-400/10'
            };
        }
        return {
            primary: 'bg-blue-600',
            primaryHover: 'hover:bg-blue-700',
            primaryText: 'text-blue-500',
            bgGradient: 'from-blue-600 to-blue-800',
            borderFocus: 'focus:border-blue-500',
            progressBar: 'bg-blue-500',
            lightBg: 'bg-blue-500/10'
        };
    };

    const theme = mission ? getPlatformColors(mission.platform) : getPlatformColors('uber');

    // --- Actions ---

    // --- Helper: Mission Status ---
    const getMissionStatus = (m) => {
        const start = getSafeDate(m.startDate);
        const end = getSafeDate(m.deadline);
        const nowTime = now.getTime();

        // check completion
        // check completion using cumulative meta
        const totalMetaForM = m.tiers.reduce((acc, t) => acc + (parseInt(t.targetTrips) || 0), 0);
        const isComplete = m.tiers.length > 0 && m.currentTrips >= totalMetaForM;

        if (isComplete) {
            return { status: 'completed', label: 'Concluída', color: 'text-green-500', bg: 'bg-green-500/10', icon: <CheckCircle size={14} /> };
        }

        if (nowTime < start.getTime()) {
            const diff = start.getTime() - nowTime;
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

            let timeStr = "";
            if (days > 0) timeStr += `${days}d `;
            if (hours > 0) timeStr += `${hours}h `;
            timeStr += `${minutes}m`;

            return { status: 'upcoming', label: `Começa em ${timeStr}`, color: 'text-blue-500', bg: 'bg-blue-500/10', icon: <Calendar size={14} /> };
        }

        if (nowTime > end.getTime()) {
            return { status: 'expired', label: 'Expirada / Terminou', color: 'text-red-500', bg: 'bg-red-500/10', icon: <AlertTriangle size={14} /> };
        }

        // Active
        const diff = end.getTime() - nowTime;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        let timeStr = "";
        if (days > 0) timeStr = `${days} dias`;
        else timeStr = `${hours} horas`;

        return { status: 'active', label: `Termina em ${timeStr}`, color: 'text-orange-500', bg: 'bg-orange-500/10', icon: <Clock size={14} /> };
    };

    const handleCreateNew = () => {
        if (isExpired) {
            onOpenPremium();
            return;
        }
        const nowTime = new Date();
        const nextMonday = new Date();
        nextMonday.setDate(nextMonday.getDate() + (1 + 7 - nextMonday.getDay()) % 7);
        nextMonday.setHours(4, 0, 0, 0);

        setEditingMission({
            ...emptyMissionTemplate,
            id: crypto.randomUUID(),
            startDate: nowTime.toISOString(),
            deadline: nextMonday.toISOString(),
            title: "Nova Missão",
            tiers: [{ id: '1', targetTrips: '', reward: '', isCompleted: false }]
        });
        setViewMode('create');
    };

    const handleEditCurrent = () => {
        if (isExpired) {
            onOpenPremium();
            return;
        }
        setEditingMission(JSON.parse(JSON.stringify(activeMission))); // Deep copy
        setViewMode('edit');
    };

    const handleSave = () => {
        if (isExpired) {
            onOpenPremium();
            return;
        }
        if (!editingMission.title) return alert("Dê um nome para a missão.");

        if (viewMode === 'create') {
            setMissions(prev => [...prev, editingMission]);
            setActiveMissionId(editingMission.id);
            setViewMode('details');
        } else if (viewMode === 'edit') {
            setMissions(prev => prev.map(m => m.id === editingMission.id ? editingMission : m));
            setActiveMissionId(editingMission.id);
            setViewMode('details');
        }
    };

    const handleDelete = (id = null, e = null) => {
        if (e) e.stopPropagation();

        if (isExpired) {
            onOpenPremium();
            return;
        }

        const targetId = id || (viewMode === 'edit' ? editingMission.id : activeMissionId);

        if (setConfirmModal) {
            setConfirmModal({
                type: 'delete',
                title: 'Excluir Missão',
                message: 'Tem certeza que deseja excluir esta missão permanentemente? Todo o progresso será perdido.',
                confirmText: 'Excluir',
                onConfirm: () => {
                    setMissions(prev => prev.filter(m => m.id !== targetId));
                    if (!id) {
                        setViewMode('list');
                        setActiveMissionId(null);
                    }
                    if (setConfirmModal) setConfirmModal(null);
                }
            });
        }
    };

    const handleTogglePin = (id, e) => {
        if (e) e.stopPropagation();
        
        const missionToToggle = missions.find(m => m.id === id);
        if (!missionToToggle) return;
        
        if (!missionToToggle.isPinned) {
            const pinnedCount = missions.filter(m => m.isPinned).length;
            if (pinnedCount >= 2) {
                if (setConfirmModal) {
                    setConfirmModal({
                        title: 'Atenção',
                        message: 'Você só pode fixar até 2 missões na tela inicial ao mesmo tempo.',
                        confirmText: 'Entendi',
                        onConfirm: () => {
                            if (setConfirmModal) setConfirmModal(null);
                        }
                    });
                } else {
                    alert('Você só pode fixar até 2 missões ao mesmo tempo.');
                }
                return;
            }
        }
        
        setMissions(prev => prev.map(m => m.id === id ? { ...m, isPinned: !m.isPinned } : m));
    };

    // Field Updaters for Editing
    const updateMissionField = (field, value) => setEditingMission(prev => ({ ...prev, [field]: value }));
    const updateTier = (index, field, value) => {
        const newTiers = [...editingMission.tiers];
        if (newTiers[index]) {
            newTiers[index] = { ...newTiers[index], [field]: value };
            setEditingMission(prev => ({ ...prev, tiers: newTiers }));
        }
    };
    const addTier = () => {
        const newTier = { id: crypto.randomUUID(), targetTrips: '', reward: '', isCompleted: false };
        setEditingMission(prev => ({ ...prev, tiers: [...prev.tiers, newTier] }));
    };
    const removeTier = (index) => {
        setEditingMission(prev => ({ ...prev, tiers: prev.tiers.filter((_, i) => i !== index) }));
    };

    // Live Updaters for Details View (Directly updates missions array)
    const updateLiveMissionTrips = (newTrips) => {
        if (isExpired) {
            onOpenPremium();
            return;
        }
        setMissions(prev => prev.map(m => m.id === activeMissionId ? { ...m, currentTrips: Math.max(0, newTrips) } : m));
    };

    // --- Render: LIST VIEW ---
    if (viewMode === 'list') {
        return (
            <div className={`w-full max-w-5xl mx-auto ${styles.bg} ${styles.text} font-sans select-none pb-10 min-h-[500px] p-4`}>
                <div className="flex justify-between items-center mb-6 sticky top-0 bg-inherit z-10 py-2">
                    <h2 className="text-2xl font-bold">Suas Missões</h2>
                    <button onClick={handleCreateNew} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg shadow-indigo-500/30 transition-all active:scale-95">
                        <Plus size={18} strokeWidth={3} />
                        Nova
                    </button>
                </div>

                {missions.length === 0 ? (
                    <div className="text-center py-10 opacity-60">
                        <Target size={48} className="mx-auto mb-4 text-gray-400" />
                        <p className="text-sm">Nenhuma missão ativa.</p>
                        <p className="text-xs mt-1">Clique em "Nova" para começar.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {missions.map(m => {
                            const mTheme = getPlatformColors(m.platform);
                            const mTierIndex = m.tiers.findIndex(t => m.currentTrips < t.targetTrips);
                            const mIsComplete = m.tiers.length > 0 && mTierIndex === -1;
                            const mActiveTier = mIsComplete ? m.tiers[m.tiers.length - 1] : (mTierIndex >= 0 ? m.tiers[mTierIndex] : null);
                            const percent = mActiveTier ? Math.min(100, Math.max(0, (m.currentTrips / mActiveTier.targetTrips) * 100)) : 0;
                            const status = getMissionStatus(m);

                            return (
                                <div key={m.id}
                                    className={`group relative overflow-hidden rounded-2xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'} p-4 transition-all hover:shadow-md active:scale-[0.99]`}
                                    onClick={() => { setActiveMissionId(m.id); setViewMode('details'); }}>

                                    <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${mTheme.progressBar}`}></div>

                                    <div className="flex justify-between items-start mb-3 pl-3">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded uppercase ${m.platform === 'uber' ? 'bg-black text-white' : 'bg-yellow-400 text-black'}`}>
                                                    {m.platform}
                                                </span>
                                                <h3 className="font-bold text-lg leading-none">{m.title}</h3>
                                            </div>
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${status.color} ${status.bg} inline-block mt-1`}>
                                                {status.label}
                                            </span>
                                        </div>

                                        <div className="flex gap-1 z-10">
                                            <button
                                                onClick={(e) => handleTogglePin(m.id, e)}
                                                className={`p-2 -mt-2 hover:text-indigo-500 rounded-full transition-colors ${m.isPinned ? 'text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30' : 'text-gray-300'}`}
                                                title={m.isPinned ? "Desafixar" : "Fixar no Painel Principal"}
                                            >
                                                <Pin size={18} fill={m.isPinned ? "currentColor" : "none"} />
                                            </button>
                                            <button
                                                onClick={(e) => handleDelete(m.id, e)}
                                                className="p-2 -mr-2 -mt-2 text-gray-300 hover:text-red-500 hover:bg-red-500/10 rounded-full transition-colors"
                                                title="Excluir Missão"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="pl-3">
                                        <div className="flex justify-between text-xs font-bold mb-1.5 align-bottom">
                                            <span className={mIsComplete ? 'text-green-500' : styles.text}>{m.currentTrips} viagens</span>
                                            <span className={styles.subtext}>{mIsComplete ? 'Concluído' : `Meta: ${mActiveTier?.targetTrips}`}</span>
                                        </div>
                                        <div className={`w-full rounded-full h-2 overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                            <div className={`h-full ${mIsComplete ? 'bg-green-500' : mTheme.progressBar}`} style={{ width: `${mIsComplete ? 100 : percent}%` }}></div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // --- Render: CREATE / EDIT MODE ---
    if (viewMode === 'create' || viewMode === 'edit') {
        return (
            <div className={`w-full max-w-lg mx-auto ${styles.bg} ${styles.text} font-sans select-none pb-10 p-4 animate-fade-in-up`}>
                <div className="flex justify-between items-center mb-4 sticky top-0 z-10 bg-inherit py-2 backdrop-blur-md">
                    <div className="flex items-center gap-3">
                        <button onClick={() => {
                            if (viewMode === 'create') setViewMode('list');
                            else setViewMode('details');
                        }} className={`p-2 rounded-full ${isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}>
                            <ArrowRight className="rotate-180" size={20} />
                        </button>
                        <h1 className="text-lg font-bold">{viewMode === 'create' ? 'Nova Missão' : 'Editar Missão'}</h1>
                    </div>
                </div>


                <div className="space-y-6">
                    {/* Platform Select */}
                    <div>
                        <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${styles.subtext}`}>Plataforma</label>
                        <div className={`grid grid-cols-2 p-1 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                            <button onClick={() => updateMissionField('platform', 'uber')} className={`py-2 rounded-lg text-xs font-bold transition-all ${editingMission.platform === 'uber' ? 'bg-black text-white shadow' : 'text-gray-500'}`}>Uber</button>
                            <button onClick={() => updateMissionField('platform', '99')} className={`py-2 rounded-lg text-xs font-bold transition-all ${editingMission.platform === '99' ? 'bg-yellow-400 text-black shadow' : 'text-gray-500'}`}>99</button>
                        </div>
                    </div>

                    {/* Title */}
                    <div>
                        <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${styles.subtext}`}>Nome da Missão</label>
                        <input type="text" value={editingMission.title} onChange={(e) => updateMissionField('title', e.target.value)} className={`w-full rounded-xl p-3 font-semibold focus:outline-none focus:ring-2 ${theme.borderFocus} border ${styles.inputBg}`} placeholder="Ex: Promoção Semanal" />
                    </div>

                    {/* Current Trips */}
                    <div className={`p-4 rounded-2xl border ${theme.lightBg} border-transparent`}>
                        <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${theme.primaryText}`}>Já fez quantas viagens?</label>
                        <div className="flex items-center gap-3">
                            <Car className={theme.primaryText} size={24} />
                            <input type="number" value={editingMission.currentTrips === 0 ? '' : editingMission.currentTrips} onChange={(e) => updateMissionField('currentTrips', e.target.value === '' ? 0 : parseInt(e.target.value))} className={`w-full rounded-xl p-3 text-2xl font-bold focus:outline-none bg-transparent border-dashed border-2 ${isDark ? 'border-white/20' : 'border-black/10'}`} placeholder="0" />
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${styles.subtext}`}>Início</label>
                            <input type="datetime-local" value={formatDateForInput(editingMission.startDate)} onChange={(e) => updateMissionField('startDate', new Date(e.target.value).toISOString())} className={`w-full rounded-xl p-3 border ${styles.inputBg} focus:outline-none focus:ring-2 ${theme.borderFocus} text-xs font-medium`} />
                        </div>
                        <div>
                            <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${styles.subtext}`}>Fim (Prazo)</label>
                            <input type="datetime-local" value={formatDateForInput(editingMission.deadline)} onChange={(e) => updateMissionField('deadline', new Date(e.target.value).toISOString())} className={`w-full rounded-xl p-3 border ${styles.inputBg} focus:outline-none focus:ring-2 ${theme.borderFocus} text-xs font-medium`} />
                        </div>
                    </div>

                    {/* Tiers */}
                    <div className={`border-t pt-6 ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-base">Fases e Metas</h3>
                            <button onClick={addTier} className={`text-xs font-bold px-3 py-1.5 rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500/20 flex items-center gap-1 transition-colors`}>
                                <Plus size={14} /> Nova Fase
                            </button>
                        </div>
                        <div className="space-y-3">
                            {editingMission.tiers.map((tier, index) => (
                                <div key={tier.id} className={`p-4 rounded-2xl border relative transition-all ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}`}>
                                    <div className={`flex justify-between items-center text-xs font-bold uppercase tracking-wider mb-3 ${styles.subtext}`}>
                                        <span>Fase {index + 1}</span>
                                        <button onClick={() => removeTier(index)} className="text-red-400 hover:text-red-500 p-1 hover:bg-red-500/10 rounded-full transition-colors"><Trash2 size={14} /></button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={`text-[10px] ${styles.subtext} block mb-1`}>Viagens ADICIONAIS desta Fase</label>
                                            <input type="number" value={tier.targetTrips} onChange={(e) => updateTier(index, 'targetTrips', e.target.value === '' ? '' : parseInt(e.target.value))} className={`w-full rounded-lg p-2 font-bold ${styles.inputBg} focus:outline-none border focus:border-indigo-500`} placeholder="Ex: 20" />
                                        </div>
                                        <div>
                                            <label className={`text-[10px] ${styles.subtext} block mb-1`}>Bônus desta Fase (R$)</label>
                                            <input type="number" value={tier.reward} onChange={(e) => updateTier(index, 'reward', e.target.value === '' ? '' : parseFloat(e.target.value))} className={`w-full rounded-lg p-2 font-bold ${styles.inputBg} focus:outline-none border focus:border-green-500 text-green-500`} placeholder="R$" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8 space-y-3">
                            <button onClick={handleSave} className={`w-full py-3.5 rounded-xl ${theme.primary} ${theme.primaryHover} text-white text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20 active:scale-95 transition-transform`}>
                                <CheckCircle size={16} /> Salvar Missão
                            </button>
                            <button onClick={() => handleDelete()} className="w-full py-3 rounded-xl border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors text-sm font-bold flex items-center justify-center gap-2">
                                <Trash2 size={16} /> Excluir Missão
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    // --- Render: DETAILS VIEW ---
    return (
        <div className={`w-full max-w-5xl mx-auto ${styles.bg} ${styles.text} font-sans select-none pb-10 p-4 animate-fade-in`}>
            <header className="flex justify-between items-center bg-inherit mb-5">
                <div className="flex items-center gap-2">
                    <button onClick={() => setViewMode('list')} className={`p-2 rounded-full ${isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}>
                        <ArrowRight className="rotate-180" size={20} />
                    </button>
                    <ToolHeader isDark={isDark} title={mission.title} platform={mission.platform} />
                </div>
                <div className="flex gap-2">
                    <button onClick={handleEditCurrent} className={`p-2.5 rounded-xl border shadow-sm transition-all active:scale-95 ${styles.iconBtn}`}>
                        <Settings size={20} />
                    </button>
                </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <div className="space-y-5">
                    {/* Status Card Principal */}
                    <Card isDark={isDark} borderColor={mission.platform === 'uber' ? 'border-t-4 border-t-green-500' : 'border-t-4 border-t-yellow-400'} className="relative overflow-hidden mb-5">
                        {isAllCompleted ? (
                            <div className="text-center py-8">
                                <div className="inline-block p-4 rounded-full bg-green-500/20 mb-4 animate-bounce-slow">
                                    <CheckCircle className={`w-16 h-16 text-green-500`} />
                                </div>
                                <h2 className="text-2xl font-bold mb-1">Missão Concluída com Sucesso!</h2>
                                <p className={`text-sm ${styles.subtext} mb-6`}>Você bateu todas as fases e garantiu o prêmio máximo.</p>
                                <div className="bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg shadow-green-500/30 inline-block px-8 py-4 rounded-2xl">
                                    <span className="text-xs uppercase font-bold text-white/80 tracking-wider block mb-1">Bônus Total</span>
                                    <div className="text-4xl font-black text-white px-2 drop-shadow-sm">{formatCurrency(getCumulativeReward(mission.tiers.length - 1))}</div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-2 mb-4">
                                    <span className={`text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 ${getMissionStatus(mission).bg} ${getMissionStatus(mission).color}`}>
                                        {getMissionStatus(mission).icon} {getMissionStatus(mission).label}
                                    </span>
                                </div>
                                <div className="flex justify-between items-end mb-4">
                                    <div>
                                        <span className={`${styles.subtext} text-xs uppercase font-bold tracking-wider block mb-1`}>Restam Apenas</span>
                                        <div className="flex items-baseline gap-1">
                                            <span className={`text-6xl font-black tracking-tighter ${styles.text}`}>{tripsRemainingForTier}</span>
                                            <span className={`text-xl font-medium ${styles.subtext}`}>viagens</span>
                                        </div>
                                    </div>
                                    <div className="text-right flex flex-col items-end">
                                        <div className={`text-xs mb-1 font-bold uppercase ${styles.subtext}`}>Próximo Bônus</div>
                                        <div className={`text-2xl font-black ${theme.primaryText} leading-none mb-1`}>{formatCurrency(activeTier?.reward ?? 0)}</div>
                                        <div className={`text-[10px] font-bold ${styles.subtext} bg-slate-500/10 px-2 py-0.5 rounded`}>Meta Total: {totalMeta}</div>
                                        <div className={`text-[10px] font-bold ${styles.subtext} bg-green-500/10 text-green-500 px-2 py-0.5 rounded mt-1`}>Total Potencial: {formatCurrency(totalPotentialBonus)}</div>
                                    </div>
                                </div>

                                <div className="space-y-3 mt-6">
                                    <div className={`flex justify-between text-xs font-bold uppercase tracking-wider items-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                        {isQuickEditingTrips ? (
                                            <div className="flex items-center gap-2 animate-fade-in">
                                                <input ref={quickEditInputRef} type="number" value={mission.currentTrips} onChange={(e) => updateLiveMissionTrips(parseInt(e.target.value) || 0)} onBlur={() => { setIsQuickEditingTrips(false) }} className={`w-20 rounded-lg p-1 text-center font-bold text-sm border focus:outline-none focus:border-indigo-500 ${styles.inputBg}`} />
                                                <button onClick={() => { setIsQuickEditingTrips(false) }} className="bg-green-500 text-white p-1 rounded-lg"><CheckCircle size={16} /></button>
                                            </div>
                                        ) : (
                                            <button onClick={() => setIsQuickEditingTrips(true)} className={`flex items-center gap-2 hover:text-indigo-500 transition-colors p-1.5 rounded-lg -ml-1.5 hover:bg-indigo-50/10`}>
                                                <span className={isDark ? 'text-white' : 'text-slate-900'}>{mission.currentTrips} REALIZADAS</span>
                                                <Pencil size={12} className="opacity-50" />
                                            </button>
                                        )}
                                        <span>FASE ATUAL: {activeCumulativeMeta}</span>
                                    </div>
                                    <ProgressBar isDark={isDark} current={mission.currentTrips} total={activeCumulativeMeta} color={theme.progressBar} />
                                </div>
                            </>
                        )}
                    </Card>

                    {/* Botão de Ação Rápida */}
                    <div className="grid grid-cols-4 gap-3 mb-5">
                        <button onClick={() => updateLiveMissionTrips(mission.currentTrips - 1)} className={`col-span-1 rounded-2xl flex items-center justify-center h-16 disabled:opacity-50 transition-all active:scale-95 ${styles.buttonDanger}`}>
                            <MinusCircle size={24} />
                        </button>
                        <button onClick={() => updateLiveMissionTrips(mission.currentTrips + 1)} disabled={isAllCompleted} className={`col-span-2 ${isAllCompleted ? 'bg-gray-300 cursor-not-allowed' : theme.primary} ${!isAllCompleted && theme.primaryHover} rounded-2xl flex items-center justify-center gap-2 h-16 text-white font-bold text-xl shadow-xl shadow-blue-900/20 transition-all transform active:scale-95`}>
                            <Plus size={28} strokeWidth={3} />
                            {isAllCompleted ? "FIM" : "1 VIAGEM"}
                        </button>
                        <button onClick={() => setShowAddTripsModal(true)} disabled={isAllCompleted} className={`col-span-1 rounded-2xl flex items-center justify-center h-16 disabled:opacity-50 transition-all active:scale-95 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold border ${styles.borderSeparator}`}>
                            <span className="text-xs font-black">VÁRIAS</span>
                        </button>
                    </div>
                </div>

                <div className="space-y-5">
                    {/* Cards de Métricas */}
                    {!isAllCompleted && (
                        <div className="space-y-3 pt-2">
                            <div className={`p-4 rounded-2xl border flex items-start gap-4 transition-colors ${isPaceDanger ? 'bg-red-500/10 border-red-500/30' : isPaceWarning ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                                <div className={`p-2 rounded-full ${isPaceDanger ? 'bg-red-500/20 text-red-500' : isPaceWarning ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}>
                                    {isPaceDanger ? <AlertTriangle size={20} /> : isPaceWarning ? <Clock size={20} /> : <CheckCircle size={20} />}
                                </div>
                                <div>
                                    <h4 className={`text-base font-bold ${isPaceDanger ? 'text-red-500' : isPaceWarning ? 'text-yellow-500' : 'text-green-500'}`}>
                                        {isPaceDanger ? 'Acelere o Ritmo!' : isPaceWarning ? 'Ritmo Moderado' : 'Ritmo Tranquilo'}
                                    </h4>
                                    <p className={`text-xs mt-1 leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                                        {isPaceDanger
                                            ? <span>Você precisa de <strong className="text-red-400">{Math.ceil(paceNeeded)} {usePaceByDay ? 'viagens/dia' : 'viagens/hora'}</strong> para bater a meta. Acelere!</span>
                                            : 'Você está no prazo. Continue assim que vai dar bão.'}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3">
                                <div className={`p-4 rounded-2xl border flex items-center justify-between transition-all bg-indigo-500/10 border-indigo-500/30 overflow-hidden relative group`}>
                                    <div className="flex items-center gap-3">
                                        <div className="p-2.5 rounded-xl bg-indigo-500 text-white shadow-lg shadow-indigo-500/30">
                                            <Zap size={20} fill="currentColor" />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold">Ritmo Geral (Bater Tudo)</h4>
                                            <p className={`text-[10px] ${styles.subtext}`}>Para completar todas as fases</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-black text-indigo-500 leading-none mb-0.5">{Math.ceil(generalPaceNeeded)}</div>
                                        <div className={`text-[10px] font-bold uppercase ${styles.subtext}`}>{usePaceByDay ? 'por dia' : 'por hora'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Card isDark={isDark} className="flex flex-col justify-between min-h-[100px]">
                                    <div className={`flex items-center gap-2 mb-2 ${styles.subtext}`}>
                                        <Clock size={16} />
                                        <span className="text-[10px] font-bold uppercase tracking-wider">Ritmo para Fase {activeTierIndex + 1}</span>
                                    </div>
                                    <div>
                                        <div className={`text-3xl font-black leading-none ${isPaceDanger ? 'text-red-500' : isPaceWarning ? 'text-yellow-500' : 'text-green-500'}`}>
                                            {Math.ceil(paceNeeded)}
                                        </div>
                                        <span className={`text-[10px] font-bold ${styles.subtext}`}>{usePaceByDay ? 'viagens / dia' : 'viagens / hora'}</span>
                                    </div>
                                </Card>

                                <Card isDark={isDark} className="flex flex-col justify-between min-h-[100px]">
                                    <div className={`flex items-center gap-2 mb-2 ${styles.subtext}`}>
                                        <DollarSign size={16} />
                                        <span className="text-[10px] font-bold uppercase tracking-wider">Valor Extra</span>
                                    </div>
                                    <div>
                                        <div className={`text-3xl font-black leading-none ${theme.primaryText}`}>
                                            {formatCurrency(valuePerTripInTier).split(',')[0]}<span className="text-lg">,{formatCurrency(valuePerTripInTier).split(',')[1]}</span>
                                        </div>
                                        <span className={`text-[10px] font-bold ${styles.subtext}`}>por corrida (nesta fase)</span>
                                    </div>
                                </Card>
                            </div>

                            <div className="flex justify-between items-center px-2 pt-2">
                                <div className={`flex items-center gap-2 text-xs font-medium ${styles.subtext}`}>
                                    <Calendar size={12} />
                                    <span>Início: <strong>{formatShortDate(startDate)}</strong></span>
                                </div>
                                <div className={`flex items-center gap-2 text-xs font-medium ${styles.subtext}`}>
                                    <Calendar size={12} />
                                    <span>Fim: <strong>{formatShortDate(deadlineDate)}</strong></span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Progresso Visual das Fases */}
                    <div className="mt-5 space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wider mb-3 px-1">Progresso das Fases</h4>
                        {mission.tiers.map((tier, idx) => {
                            const tierCumulativeMeta = getCumulativeMeta(idx);
                            const isCompleted = mission.currentTrips >= tierCumulativeMeta;
                            const isCurrent = activeTierIndex === idx;
                            const tripsLeftForThisTier = Math.max(0, tierCumulativeMeta - mission.currentTrips);

                            return (
                                <div key={tier.id} className={`p-4 rounded-xl border flex items-center justify-between transition-all ${isCompleted ? 'bg-green-500/10 border-green-500/30' : (isCurrent ? 'bg-blue-500/10 border-blue-500/30' : isDark ? 'bg-slate-800 border-slate-700 opacity-60' : 'bg-gray-50 border-gray-200 opacity-60')}`}>
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 flex items-center justify-center rounded-full ${isCompleted ? 'bg-green-500 text-white shadow-lg shadow-green-500/30' : isCurrent ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30' : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-400'}`}>
                                            {isCompleted ? <CheckCircle size={20} /> : <Target size={20} />}
                                        </div>
                                        <div>
                                            <div className="font-bold text-sm leading-none mb-1">Fase {idx + 1}</div>
                                            <div className={`text-xs ${isCompleted ? 'text-green-500 font-bold' : isCurrent ? 'text-blue-500 font-bold' : styles.subtext}`}>
                                                {isCompleted ? 'Concluída' : isCurrent ? 'Fase Atual' : `Faltam ${tripsLeftForThisTier} viagens acumuladas`}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`font-black text-lg leading-none mb-0.5 ${isCompleted ? 'text-green-500' : isCurrent ? 'text-blue-500' : styles.subtext}`}>{formatCurrency(tier.reward)}</div>
                                        <div className={`text-[10px] font-bold uppercase ${styles.subtext}`}>Meta: {tierCumulativeMeta} viagens</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <AddTripsModal
                    isOpen={showAddTripsModal}
                    isDark={isDark}
                    theme={theme}
                    onClose={() => setShowAddTripsModal(false)}
                    onConfirm={(num) => updateLiveMissionTrips(mission.currentTrips + num)}
                />
            </div>
        </div>
    );
}
